/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PropertiesModel } from "../housien-models/PropertiesModel";
import type { PropertyIn } from "../housien-models/PropertyIn";
import type { SuccessResponse } from "../housien-models/SuccessResponse";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPIHousien } from "../core/OpenAPI";
import { request as __request } from "../core/request";
import { PropertyResponseType, RealEstateProperty } from "@services/housien/types";

/**
 * PropertiesService class.
 */
export interface PropertyVideoFile {
  id: string;
  url: string;
  content_type: string;
}

export interface PropertyVideoUploadLink {
  video_id: string;
  upload: { url: string; fields: Record<string, string> };
}

export class PropertiesService {
  /**
   * Add Property to Agency
   * Add a new property to the specified agency.
   * @param agencyId The ID of the agency.
   * @param requestBody
   * @returns SuccessResponse Property added successfully.
   * @throws ApiError
   */
  public static postProperty(
    agencyId: string,
    requestBody: PropertyIn
  ): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
      method: "POST",
      url: "/agencies/{agencyId}/properties",
      path: {
        agencyId: agencyId,
      },
      body: requestBody,
      mediaType: "application/json",
    });
  }

  /**
   * Get All Properties of an Agency
   * Retrieve all properties associated with a specified agency.
   * @param agencyId The ID of the agency.
   * @param agencyId The ID of the agency.
   * @returns PropertiesModel Properties retrieved successfully.
   * @throws ApiError
   */
  public static getAllProperties(
    agencyId: string
  ): CancelablePromise<Array<PropertiesModel>> {
    return __request(OpenAPIHousien, {
      method: "GET",
      url: "/agencies/{agencyId}/properties",
      path: {
        agencyId: agencyId,
      },
    });
  }

  /**
   * Get Property Information
   * Retrieve property information based on the property ID within a specified agency.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @returns PropertiesModel Property information retrieved successfully.
   * @throws ApiError
   */
  public static getProperty(
    agencyId: string,
    propertyId: string
  ): CancelablePromise<PropertyResponseType> {
    return __request(OpenAPIHousien, {
      method: "GET",
      url: "/agencies/{agencyId}/properties/{propertyId}",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
    });
  }

  /**
   * Get Link Image Property Information
   * Retrieve property information based on the property ID within a specified agency.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @returns PropertiesModel Property information retrieved successfully.
   * @throws ApiError
   */
  public static getLinkImageProperty(
    agencyId: string,
    propertyId: string
  ): CancelablePromise<string> {
    return __request(OpenAPIHousien, {
      method: "GET",
      url: "/agencies/{agencyId}/properties/{propertyId}/images",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
    });
  }

  /**
   * Delete Property
   * Delete property based on the property ID within a specified agency.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @returns SuccessResponse Property deleted successfully.
   * @throws ApiError
   */
  public static deleteProperty(
    agencyId: string,
    propertyId: string
  ): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
      method: "DELETE",
      url: "/agencies/{agencyId}/properties/{propertyId}",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
    });
  }

  /**
   * Delete Property
   * Delete property based on the property ID within a specified agency.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @returns SuccessResponse Property deleted successfully.
   * @throws ApiError
   */
  public static updateProperty(
      propertyId: string,
      requestBody: Partial<RealEstateProperty>,
      agencyId?: string,
  ): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
      method: "PUT",
      url: "/agencies/{agencyId}/properties/{propertyId}",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
      body: requestBody,
      mediaType: "application/json",
    });
  }

  /**
   * Delete Property Image
   * Delete a specific image associated with a property and agency.
   * @param agencyId The ID of the agency managing the property.
   * @param propertyId The ID of the property from which the image is being deleted.
   * @param url The URL of the image to be deleted.
   * @returns SuccessResponse Image deleted successfully.
   * @throws ApiError
   */
  public static deletePropertyImage(
    agencyId: string,
    propertyId: string,
    url: string
  ): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
      method: "DELETE",
      url: "/agencies/{agencyId}/properties/{propertyId}/images",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
      query: {
        url: url,
      },
    });
  }

  /**
   * Get presigned view URLs for all images of a property.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @returns Object containing an array of presigned image URLs.
   * @throws ApiError
   */
  public static getPropertyImageUrls(
    agencyId: string,
    propertyId: string
  ): CancelablePromise<{ urls: string[] }> {
    return __request(OpenAPIHousien, {
      method: "GET",
      url: "/agencies/{agencyId}/properties/{propertyId}/images/view",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
    });
  }
  /**
   * List the uploaded video files of a property, with playable signed URLs.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @returns The uploaded videos.
   * @throws ApiError
   */
  public static getPropertyVideos(
    agencyId: string,
    propertyId: string
  ): CancelablePromise<{ videos: PropertyVideoFile[] }> {
    return __request(OpenAPIHousien, {
      method: "GET",
      url: "/agencies/{agencyId}/properties/{propertyId}/videos",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
    });
  }

  /**
   * Request a presigned POST for one video file.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @param contentType The file's MIME type (mp4, quicktime or webm).
   * @param size The file size in bytes.
   * @returns The new video id and the presigned POST target.
   * @throws ApiError 400 for an unsupported type or size, 409 when the property already has 5 videos.
   */
  public static getPropertyVideoUploadLink(
    agencyId: string,
    propertyId: string,
    contentType: string,
    size: number
  ): CancelablePromise<PropertyVideoUploadLink> {
    return __request(OpenAPIHousien, {
      method: "POST",
      url: "/agencies/{agencyId}/properties/{propertyId}/videos/upload-link",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
      },
      body: { content_type: contentType, size },
      mediaType: "application/json",
    });
  }

  /**
   * Delete one uploaded video file.
   * @param agencyId The ID of the agency.
   * @param propertyId The ID of the property.
   * @param videoId The video id returned by the listing.
   * @throws ApiError 404 when the video does not exist.
   */
  public static deletePropertyVideo(
    agencyId: string,
    propertyId: string,
    videoId: string
  ): CancelablePromise<unknown> {
    return __request(OpenAPIHousien, {
      method: "DELETE",
      url: "/agencies/{agencyId}/properties/{propertyId}/videos/{videoId}",
      path: {
        agencyId: agencyId,
        propertyId: propertyId,
        videoId: videoId,
      },
    });
  }
}
